import clsx from "clsx";
import { HeroInlineLogo } from "@/components/dataTable";
import {
  formatAbsenceBoardLabel,
  formatAbsenceHalfDayPart,
  getAbsenceTypeLabel,
  isHalfDayAbsence,
} from "@/lib/employeeAbsences";
import type { AbsenceType } from "@/lib/employeeAbsences";
import type { PlanungAbsence } from "@/lib/montageplanung";
import type { BarGeom } from "./AssignmentBar";
import { absenceBarPosition } from "./assignmentLayout";
import { VACATION_CREDITED_COLOR } from "./vacationSicknessOverlay";

const ABSENCE_COLORS: Record<AbsenceType, string> = {
  urlaub: "#0a84ff",
  krank: "#ff375f",
  kindkrank: "#ff9f0a",
  ueberstundenabbau: "#bf5af2",
  sonderurlaub: "#30b0c7",
  schulung: "#30d158",
  unbezahlt: "#8e8e93",
  nicht_verfuegbar: "#8e8e93",
};

export interface AbsenceBarProps {
  absence: PlanungAbsence;
  geom: BarGeom;
  dayWidth: number;
  barHeight: number;
  barTop: number;
  canApprove?: boolean;
  onApproveClick?: (absence: PlanungAbsence) => void;
}

export function AbsenceBar({
  absence,
  geom,
  dayWidth,
  barHeight,
  barTop,
  canApprove,
  onApproveClick,
}: AbsenceBarProps) {
  const { left, width } = absenceBarPosition(geom, absence, dayWidth);
  const credited = Boolean(absence.vacation_credited);
  const color = credited
    ? VACATION_CREDITED_COLOR
    : (ABSENCE_COLORS[absence.absence_type] ?? "#8e8e93");
  const fromHero = Boolean(absence.from_hero);
  const fromBridge = Boolean(absence.from_bridge);
  const fromBetriebsferien = Boolean(absence.from_betriebsferien);
  const fromAutoCalendar = fromBridge || fromBetriebsferien;
  const sourceLabel = fromHero
    ? "Hero"
    : fromBetriebsferien
      ? "Betriebsferien"
      : fromBridge
        ? "Brückentag"
        : "App";
  const halfDay = isHalfDayAbsence(absence);
  const label =
    halfDay || geom.span < 2
      ? formatAbsenceBoardLabel(absence)
      : getAbsenceTypeLabel(absence.absence_type);
  const pending = absence.status === "pending";
  const clickable = Boolean(
    canApprove && pending && absence.id > 0 && onApproveClick && !fromHero && !fromAutoCalendar,
  );
  const pendingTitle = fromHero
    ? " (in Hero noch nicht genehmigt)"
    : " (beantragt – zum Freigeben klicken)";
  const halfTitle = halfDay
    ? ` · ${formatAbsenceHalfDayPart(absence.half_day_part, "long")}`
    : "";

  return (
    <div
      className={clsx(
        "mp-absence",
        pending && "mp-absence-pending",
        credited && "mp-absence-credited",
        clickable && "mp-absence-clickable",
        halfDay && "mp-absence-half",
      )}
      style={{
        left,
        width,
        top: barTop,
        height: barHeight,
        backgroundColor: `${color}40`,
        color,
        borderColor: color,
        boxShadow: `inset 0 0 0 1px ${color}66`,
        ["--mp-absence-color" as string]: color,
      }}
      title={`${sourceLabel}: ${getAbsenceTypeLabel(absence.absence_type)}${
        credited
          ? " (krankgeschrieben – Urlaub wird gutgeschrieben)"
          : pending
            ? pendingTitle
            : ""
      } · ${absence.date_from.slice(0, 10)} – ${absence.date_to.slice(0, 10)}${halfTitle}${
        !fromHero && !fromAutoCalendar && absence.id > 0 ? ` · Antrag #${absence.id}` : ""
      }`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={
        clickable
          ? (e) => {
              e.stopPropagation();
              onApproveClick?.(absence);
            }
          : undefined
      }
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onApproveClick?.(absence);
              }
            }
          : undefined
      }
    >
      <span className="mp-absence-label">
        {fromHero && <HeroInlineLogo className="shrink-0" />}
        <span className="mp-absence-label-text">{label}</span>
      </span>
      {pending && !fromHero && !fromBridge && (
        <span className="mp-absence-pending-mark" aria-hidden>?</span>
      )}
    </div>
  );
}
